import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Heart, MapPin, PawPrint, Sparkles } from 'lucide-react';
import { PotentialMatch } from '@/hooks/useMatching';
import { useState } from 'react';
import { RequestMatchModal } from './RequestMatchModal';
import { useNavigate } from 'react-router-dom';

interface MatchDiscoveryCardProps {
  match: PotentialMatch;
  onRequestMatch?: () => void;
}

export const MatchDiscoveryCard = ({ match, onRequestMatch }: MatchDiscoveryCardProps) => {
  const navigate = useNavigate();
  const [showMatchModal, setShowMatchModal] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    navigate(`/pet/${match.pet_id}`);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Low';
  };

  return (
    <>
      <Card 
        className="p-3 sm:p-4 hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex gap-4">
          {/* Pet Avatar */}
          <Avatar className="h-20 w-20 sm:h-24 sm:w-24 shrink-0">
            <AvatarImage src={match.photo_url || undefined} alt={match.pet_name} />
            <AvatarFallback className="bg-pink-100">
              <PawPrint className="h-12 w-12 text-pink-500" />
            </AvatarFallback>
          </Avatar>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg truncate">{match.pet_name}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{match.species}</span>
                  {match.breed_name && (
                    <>
                      <span>•</span>
                      <span>{match.breed_name}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Compatibility Score Badge */}
              <Badge
                className={`${getScoreColor(match.compatibility_score)} text-white shrink-0`}
              >
                {match.compatibility_score}% {getScoreLabel(match.compatibility_score)}
              </Badge>
            </div>

            {/* Location */}
            {match.location && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                <MapPin className="h-3.5 w-3.5" />
                <span className="truncate">{match.location}</span>
                <span>•</span>
                <span>
                  {match.distance_km !== undefined && match.distance_km !== null
                    ? match.distance_km < 1 
                      ? `${Math.round(match.distance_km * 1000)}m away`
                      : match.distance_km <= 5 
                      ? `📍 ${match.distance_km.toFixed(1)} km`
                      : match.distance_km <= 15
                      ? `🚗 ${match.distance_km.toFixed(1)} km`
                      : match.distance_km <= 50
                      ? `🛣️ ${Math.round(match.distance_km)} km`
                      : `✈️ ${Math.round(match.distance_km)} km`
                    : match.distance_estimate}
                </span>
              </div>
            )}

            {/* Age */}
            {match.age_years > 0 && (
              <div className="text-sm text-muted-foreground mb-3">
                {match.age_years} year{match.age_years !== 1 ? 's' : ''} old
              </div>
            )}

            {/* Match Reasons (Top 3) */}
            <div className="space-y-1 mb-3">
              {match.match_reasons.slice(0, 3).map((reason, index) => (
                <div
                  key={index}
                  className="text-xs text-muted-foreground flex items-center gap-1"
                >
                  <Sparkles className="h-3 w-3 text-pink-500 shrink-0" />
                  <span className="truncate">{reason}</span>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/pet/${match.pet_id}`);
                }}
                variant="outline"
                className="flex-1"
                size="sm"
              >
                <span className="text-xs sm:text-sm">View Profile</span>
              </Button>
              
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMatchModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                size="sm"
              >
                <Heart className="h-4 w-4 mr-1" />
                <span className="text-xs sm:text-sm">Match</span>
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <RequestMatchModal
        isOpen={showMatchModal}
        onClose={() => {
          setShowMatchModal(false);
          onRequestMatch?.();
        }}
        targetPetId={match.pet_id}
        targetPetName={match.pet_name}
        targetPetSpecies={match.species}
      />
    </>
  );
};
